"use client";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { NotionEvent } from "@/lib/notion/types/eventsTypes";
import { ContainerGlob } from "../../../../../componentsGLob/ContainerGlob";
import { EventCard } from "./eventsCard";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@packages/ui/src/components/shadcn/button";
import Image from "next/image";

export default function EventsList() {
  const { t } = useTranslation("buzzCard");
  const [events, setEvents] = useState<NotionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/events");
      if (!response.ok) throw new Error("Failed to fetch events");
      const json = await response.json();
      if (!json.success)
        throw new Error(json.error || t("eventsSection.errorTitle"));
      setEvents(json.data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("eventsSection.errorTitle")
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  if (loading) {
    return (
      <ContainerGlob>
        <div
          className="flex flex-col justify-center items-center py-20"
          role="status"
          aria-live="polite"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
          <span className="text-gray-600 text-lg">
            {t("eventsSection.loading")}
          </span>
        </div>
      </ContainerGlob>
    );
  }

  if (error) {
    return (
      <ContainerGlob>
        <div className="py-20" role="alert">
          <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <h3 className="text-red-800 font-medium">
                {t("eventsSection.errorTitle")}
              </h3>
            </div>
            <p className="text-red-700 mb-4">{error}</p>
          </div>
          <div className="flex justify-center mt-6">
            <Button
              onClick={fetchEvents}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              {t("eventsSection.retry")}
            </Button>
          </div>
        </div>
      </ContainerGlob>
    );
  }

  if (events.length === 0) {
    return (
      <div className="bg-black" id="eventi">
        <ContainerGlob>
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <h1 className="text-4xl font-bold text-white mb-6">
              {t("eventsSection.emptyTitle")}
            </h1>
            <div className="relative w-24 h-24 mb-6">
              <Image
                src="/loghi/ape.png"
                alt=""
                fill
                className="object-contain"
                priority
              />
            </div>
            <h2 className="text-xl text-gray-300 max-w-md">
              {t("eventsSection.emptyDescription")}
            </h2>
          </div>
        </ContainerGlob>
      </div>
    );
  }

  return (
    <section className="py-16 md:py-20" id="eventi">
      <ContainerGlob>
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t("eventsSection.sectionTitle")}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t("eventsSection.sectionDescription")}
          </p>
        </div>
        <div className="grid gap-8 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 max-w-7xl mx-auto">
          {events.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      </ContainerGlob>
    </section>
  );
}
